import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from './components/Layout';
import TableContainer from './components/TableContainer';
import TableHeader from './components/TableHeader';
import Pagination10 from './components/Pagination10';
import PageFooter from './components/PageFooter';
import PageHeader from './components/PageHeader';
import PageContent from './components/PageContent';
import ResponsiveShadow from './components/ResponsiveShadow';
import Toolbar from './components/Toolbar';
import { API_BASE_URL } from '../config/ApiConfig';
import useDefaultModal from './components/hooks/useDefaultModal';

function PcaItensConsolidados() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const stateId = location && location.state && location.state.id ? Number(location.state.id) : null;
  const initialId = stateId ?? (params.get('id') ? Number(params.get('id')) : null);
  const [id, setId] = useState(initialId);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [filterField, setFilterField] = useState('all');
  const [paginationProps, setPaginationProps] = useState({ startIndex: 0, rowsPerPage: 10 });
  const { setDefaultModal, showDefaultModal } = useDefaultModal();

  useEffect(() => {
    const fetchItems = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const cnpj = localStorage.getItem('cnpj');
        const anoBase = Number(localStorage.getItem('anoBase'));
        if (!id) {
          setItems([]);
          setLoading(false);
          return;
        }
        const res = await axios.get(`${API_BASE_URL.PCA}/consolidado/${id}/itens`, { params: { cnpj, anoBase }, headers: { Authorization: `Bearer ${token}` } });
        setItems(res.data || []);
      } catch (err) {
        console.error('Erro ao carregar itens consolidados', err);
        showDefaultModal('error', 'Erro ao carregar itens consolidados.');
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchItems();
  }, [id]);

  // compute filtered and paginated items outside JSX
  const filteredItems = (() => {
    const q = filter ? filter.toString().toLowerCase() : '';
    if (!q) return (items || []);
    const by = filterField || 'all';
    return (items || []).filter(it => {
      const codigo = it.codigoItem ? String(it.codigoItem).toLowerCase() : '';
      const descricao = it.descricaoItem ? String(it.descricaoItem).toLowerCase() : '';
      const dfds = it.listaDfds ? String(it.listaDfds).toLowerCase() : '';
      if (by === 'codigo') return codigo.includes(q);
      // default and other selections: search only in descricao and dfds
      return descricao.includes(q) || dfds.includes(q);
    });
  })();

  const totalFiltered = filteredItems.length;
  const displayedItems = filteredItems.slice(paginationProps.startIndex, paginationProps.startIndex + paginationProps.rowsPerPage);

  const handleRowsPerPageChange = (e) => {
    const val = Number(e.target.value);
    setPaginationProps({ startIndex: 0, rowsPerPage: val });
  };
  const handleFirstItems = () => setPaginationProps(prev => ({ ...prev, startIndex: 0 }));
  const handlePreviousItems = () => setPaginationProps(prev => ({ ...prev, startIndex: Math.max(0, prev.startIndex - prev.rowsPerPage) }));
  const handleNextItems = () => setPaginationProps(prev => ({ ...prev, startIndex: Math.min(totalFiltered - prev.rowsPerPage, prev.startIndex + prev.rowsPerPage) }));
  const handleLastItems = () => setPaginationProps(prev => ({ ...prev, startIndex: Math.max(0, Math.floor((totalFiltered - 1) / prev.rowsPerPage) * prev.rowsPerPage) }));

  const exportDisplayedToCsv = () => {
    if (!displayedItems || !displayedItems.length) return;
    const headers = ['Id', 'Código Item', 'Descrição', 'DFDs', 'Qtde', 'Valor Unit', 'Valor Total'];
    const rows = displayedItems.map(it => {
      const qv = it.qtdeTotal ?? it.qtdeEstimada ?? it.quantidade ?? it.qtde;
      const v = it.vlrUnitario ?? it.vlrUnit ?? it.vlr;
      let total = '-';
      try {
        const nq = Number(qv);
        const nv = Number(v);
        if (!Number.isNaN(nq) && !Number.isNaN(nv)) {
          total = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(nq * nv);
        }
      } catch (e) { /* ignore */ }
      const valorUnit = (v === null || v === undefined || v === '') ? '-' : (Number.isNaN(Number(v)) ? String(v) : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v)));
      return [it.id ?? '', it.codigoItem ?? '', it.descricaoItem ?? '', it.listaDfds ?? '', qv ?? '', valorUnit, total];
    });
    const csv = [headers, ...rows].map(r => r.map(c => {
      if (c === null || c === undefined) return '';
      const s = String(c).replace(/"/g, '""');
      return `"${s}"`;
    }).join(';')).join('\r\n');

    // prepend UTF-8 BOM so Excel preserves accents/characters
    const bom = '\uFEFF';
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pca_itens_consolidados_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <ResponsiveShadow>
        <PageHeader title="Itens PCA Consolidados" icon="bi-box-seam" closeRoute="/pca_consolidado" backButton={false} />
        <PageContent style={{ paddingBottom: 0 }}>
          <Toolbar
            actions={[
              { key: 'export', label: 'Exportar', icon: 'bi-file-earmark-spreadsheet', variant: 'outline-secondary', onClick: () => exportDisplayedToCsv() }
            ]}
            searchValue={filter}
            onSearch={(v) => setFilter((v||'').toUpperCase())}
            searchWidth="220px"
          />

          <TableContainer tableClass="table-custom-radius">
            <colgroup>
              <col style={{ width: '6%' }} />
              <col style={{ width: '48%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '10%' }} />
            </colgroup>
            <TableHeader columns={[
              { label: 'Id', width: '6%' },
              { label: 'Descrição', width: '48%' },
              { label: 'DFDs', width: '18%' },
              { label: 'Qtde', width: '8%', align: 'right' },
              { label: 'Valor Unit', width: '10%', align: 'right' },
              { label: 'Valor Total', width: '10%', align: 'right' },
            ]} />
            <tbody>
              {loading ? (
                <tr><td colSpan="6" className="text-center">Carregando...</td></tr>
              ) : (displayedItems && displayedItems.length) ? (
                displayedItems.map(it => (
                  <tr key={`${it.cnpj}-${it.anoPca}-${it.id}-${it.codigoItem}`}>
                    <td style={{ verticalAlign: 'top' }}>{it.id}</td>
                    <td style={{ verticalAlign: 'top', wordBreak: 'break-word', whiteSpace: 'normal' }}>{it.descricaoItem}</td>
                    <td style={{ verticalAlign: 'top', wordBreak: 'break-word', whiteSpace: 'pre-line' }}>{String(it.listaDfds || '').replace(/,\s*/g, ',\n')}</td>
                    <td className="text-end" style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>{(() => {
                      const qv = it.qtdeTotal ?? it.qtdeEstimada ?? it.quantidade ?? it.qtde;
                      if (qv === null || qv === undefined || qv === '') return '-';
                      try {
                        const n = Number(qv);
                        if (Number.isNaN(n)) return String(qv);
                        return Number.isInteger(n) ? n.toString() : n.toFixed(4);
                      } catch (e) { return String(qv); }
                    })()}</td>
                    <td className="text-end" style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>{(() => {
                      const v = it.vlrUnitario ?? it.vlrUnit ?? it.vlr;
                      if (v === null || v === undefined || v === '') return '-';
                      try { const n = Number(v); return Number.isNaN(n) ? String(v) : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n); } catch (e) { return String(v); }
                    })()}</td>
                    <td className="text-end" style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>{(() => {
                      const qv = it.qtdeTotal ?? it.qtdeEstimada ?? it.quantidade ?? it.qtde;
                      const v = it.vlrUnitario ?? it.vlrUnit ?? it.vlr;
                      if (qv === null || qv === undefined || qv === '' || v === null || v === undefined || v === '') return '-';
                      try {
                        const nq = Number(qv);
                        const nv = Number(v);
                        if (Number.isNaN(nq) || Number.isNaN(nv)) return '-';
                        const total = nq * nv;
                        return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(total);
                      } catch (e) { return '-'; }
                    })()}</td>
                  </tr>
                ))
              ) : (
                <tr><td colSpan="6" className="text-center">Nenhum item encontrado.</td></tr>
              )}
            </tbody>
          </TableContainer>
        </PageContent>
        <PageFooter>
          <Pagination10
            totalItems={totalFiltered}
            rowsPerPage={paginationProps.rowsPerPage}
            startIndex={paginationProps.startIndex}
            onRowsPerPageChange={handleRowsPerPageChange}
            onFirstItems={handleFirstItems}
            onPreviousItems={handlePreviousItems}
            onNextItems={handleNextItems}
            onLastItems={handleLastItems}
          />
        </PageFooter>
      </ResponsiveShadow>
    </Layout>
  );
}

export default PcaItensConsolidados;
